import React from 'react';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiTrendingDown, FiTarget, FiBarChart3 } = FiIcons;

const PredictionTable = ({ predictions, currentPrice }) => {
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.7) return 'text-green-600 bg-green-50';
    if (confidence >= 0.5) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getPriceDirection = (predictedPrice) => {
    return predictedPrice > currentPrice ? FiTrendingUp : FiTrendingDown;
  };

  const getPriceDirectionColor = (predictedPrice) => {
    return predictedPrice > currentPrice ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-800">7-Day Price Predictions</h3>
        <div className="flex items-center text-sm text-gray-600">
          <SafeIcon icon={FiTarget} className="w-4 h-4 mr-1" />
          Current: ${currentPrice}
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-2 font-semibold">Date</th>
              <th className="text-right py-3 px-2 font-semibold">Predicted Price</th>
              <th className="text-right py-3 px-2 font-semibold">Change</th>
              <th className="text-center py-3 px-2 font-semibold">Confidence</th>
              <th className="text-left py-3 px-2 font-semibold">Key Factors</th>
            </tr>
          </thead>
          <tbody>
            {predictions.map((prediction, index) => {
              const changePercent = ((prediction.predictedPrice - currentPrice) / currentPrice) * 100;
              
              return (
                <tr key={prediction.date} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 font-medium">
                    {format(new Date(prediction.date), 'MMM dd')}
                  </td>
                  <td className="py-3 px-2 text-right">
                    <div className={`flex items-center justify-end ${getPriceDirectionColor(prediction.predictedPrice)}`}>
                      <SafeIcon 
                        icon={getPriceDirection(prediction.predictedPrice)} 
                        className="w-4 h-4 mr-1" 
                      />
                      ${prediction.predictedPrice}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className={`font-medium ${getPriceDirectionColor(prediction.predictedPrice)}`}>
                      {changePercent > 0 ? '+' : ''}{changePercent.toFixed(2)}%
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${getConfidenceColor(prediction.confidence)}`}>
                      <SafeIcon icon={FiBarChart3} className="w-3 h-3 mr-1" />
                      {(prediction.confidence * 100).toFixed(0)}%
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="text-xs text-gray-600">
                      <div>News: {(prediction.factors.newsSentiment * 100).toFixed(1)}%</div>
                      <div>Momentum: {(prediction.factors.technicalMomentum * 100).toFixed(1)}%</div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-gray-800 mb-2">Prediction Methodology</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>• <strong>News Sentiment:</strong> Average impact of recent news events</p>
          <p>• <strong>Technical Momentum:</strong> Price trend analysis</p>
          <p>• <strong>Confidence:</strong> Decreases over time horizon</p>
          <p className="text-xs text-gray-500 mt-2">
            * Predictions are based on historical patterns and current sentiment analysis. 
            Cryptocurrency markets are highly volatile and unpredictable.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PredictionTable;